import { Component, Input, computed, ViewChild, ElementRef, effect, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Node } from './node';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroChevronRight, heroDocument } from '@ng-icons/heroicons/outline';
import { FileSystemService } from '../../services/file-system.service';
import { AiService } from '../../services/ai/ai.service';
import { FormsModule } from '@angular/forms';
import { UserSettingsService } from '../../services/user-settings.service';
import { PopoverModule } from 'primeng/popover';
import { ToastService } from '../../services/toast.service';
import { UploadFileComponent } from '../dialogue/upload-file/upload-file.component';

@Component({
  selector: 'explorer',
  imports: [CommonModule, NgIcon, FormsModule, PopoverModule, UploadFileComponent],
  templateUrl: './explorer.component.html',
  styleUrl: './explorer.component.css',
  providers: [provideIcons({ heroChevronRight, heroDocument })],
})
export class ExplorerComponent implements OnInit {
  @ViewChild('newItemInput') newItemInput!: ElementRef<HTMLInputElement>;

  constructor(private fs: FileSystemService,
    private aiService: AiService,
    private cdr: ChangeDetectorRef,
    private userSettings: UserSettingsService,
    private toast: ToastService) {
    effect(() => {
      if (this.isCreatingNewItem()) {
        const focusedElem = document.activeElement;
        if (focusedElem && focusedElem instanceof HTMLElement) {
          focusedElem.blur();
        }
        this.node.active = true;
        this.cdr.detectChanges();
        this.newItemInput.nativeElement.focus();
        this.newItemInput.nativeElement.select();
      }
    });

  }
  @Input() node!: Node;
  nodeIsVisible = computed(() => this.fs.visibleNode().path === this.node.path);
  selectedNodePath = computed(() => this.fs.lastSelectedNode().path);
  nodeIsSelected = computed(() => this.fs.lastSelectedNode().path === this.node.path);
  isCreatingNewItem = computed(() => this.fs.creatingNewItem() !== null && this.selectedNodePath() === this.node.path)
  newItemName = '';
  newItemPath: Node | null = null;

  ngOnInit(): void {
    this.userSettings.serverUrl$.subscribe((url) => {
      this.node.url = url;
    });
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.confirmNewItem();
    } else if (event.key === 'Escape') {
      this.cancelNewItem();
    }
  }

  confirmNewItem() {
    this.fs.createItem(`${this.newItemPath?.path}/${this.newItemName.trim()}`, this.fs.creatingNewItem() === 'file' ? false : true, null);
    this.cancelNewItem();
  }

  cancelNewItem() {
    this.fs.creatingNewItem.set(null);
    this.newItemName = '';
    this.newItemPath = null;
  }

  onNodeClick() {
    if (this.aiService.getAiIsWritingValue()) {
      return;
    }
    if (this.node.isFolder) {
      this.node.active = !this.node.active;
    } else {
      this.fs.changeVisibleNode(this.node.path).catch((error) => {
        console.error('Error changing visible node:', error);
      });
    }
    this.fs.lastSelectedNode.set(this.node);
  }

  downloadFile(node: Node) {
    this.fs.rootNode.getNode(node.path)?.getContent().then((content) => {
      // console.log(content);
      if (content) {
        const blob = new Blob([content], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = node.name;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    });
  }

  createItem(type: 'file' | 'folder') {
    this.fs.creatingNewItem.set(type);
  }

  deleteNode(node: Node) {
    this.fs.deleteItem(node);
    this.toast.showSuccess('File deleted successfully');
  }
}